import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import ImageGallery from './ImageGallery';
import { galleryImages } from './Images';
import kidsWorkingPhoto from '../images/optimized/kidsWorkingTheGarden-web.jpg';
import inquirerMasthead from '../images/inquierMasthead.png';
import chestnutHillLocalMasthead from '../images/20250507-174843-397-chestnuthilllocal-new-masthead-2.png.jpg';
import sixAbcLogo from '../images/6AbcLogo.svg';

const instagramVideoUrl = 'https://www.instagram.com/tv/Cc5xUXsgyPK/?utm_source=ig_embed&utm_campaign=loading';
const sixAbcVideoEmbedUrl = 'https://6abc.com/video/embed?pid=10498649';
const goFundMeWidgetUrl =
  'https://www.gofundme.com/f/feed-families-fund-summer-jobs-for-teens/widget/large?attribution_id=sl%3Acba79a61-3534-4a0d-b29d-0e4a4cd5505e';

const newsArticles = [
  {
    publication: 'The Philadelphia Inquirer',
    href: 'https://www.inquirer.com/sports/pleasant-community-garden-mt-airy-marine-veteran-20210318.html?query=marine%20vet%20returns%20to%20mt%20airy',
    masthead: inquirerMasthead,
    mastheadClassName: 'max-h-28 max-w-[17rem]',
  },
  {
    publication: 'Chestnut Hill Local',
    href: 'https://www.chestnuthilllocal.com/stories/a-community-garden-grows-at-pleasant-playground,17124',
    masthead: chestnutHillLocalMasthead,
    mastheadClassName: 'max-h-24 max-w-full',
  },
  {
    publication: '6abc Action News',
    href: 'https://6abc.com/post/marine-veteran-pleasant-playground-garden-committee-philadelphia-parks-and-recreation-healthy-food-access/10491587/',
    masthead: sixAbcLogo,
    mastheadClassName: 'max-h-16 max-w-[18rem]',
    cardClassName: 'bg-[#202033]',
  },
];

function InstagramVideoEmbed() {
  useEffect(() => {
    const processEmbed = () => {
      window.instgrm?.Embeds?.process?.();
    };

    const existingScript = document.querySelector('script[src="https://www.instagram.com/embed.js"]');

    if (existingScript) {
      processEmbed();
      return;
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.instagram.com/embed.js';
    script.onload = processEmbed;
    document.body.appendChild(script);
  }, []);

  return (
    <div className="mx-auto w-full max-w-[42rem] md:ml-auto md:mr-0">
      <blockquote
        className="instagram-media"
        data-instgrm-captioned
        data-instgrm-permalink={instagramVideoUrl}
        data-instgrm-version="14"
        style={{
          background: '#fff',
          border: 0,
          borderRadius: 3,
          boxShadow: '0 0 1px 0 rgba(0,0,0,0.5), 0 1px 10px 0 rgba(0,0,0,0.15)',
          margin: '1px auto',
          maxWidth: 672,
          minWidth: 0,
          padding: 0,
          width: 'calc(100% - 2px)',
        }}
      >
        <div className="p-4 text-center">
          <a
            href={instagramVideoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold uppercase tracking-[0.18em] text-[#8e3a2b] underline-offset-4 hover:underline"
          >
            View this video on Instagram
          </a>
        </div>
      </blockquote>
    </div>
  );
}

function GoFundMeEmbed() {
  return (
    <div className="relative w-full border border-[#314935] bg-[#10351f] p-2 shadow-[0_18px_45px_rgba(16,53,31,0.16)]">
      <div className="flex items-center justify-between px-3 py-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#fbf8ef]">
        <span>GoFundMe</span>
        <span className="text-[#d7cfbd]">Secure fundraiser</span>
      </div>
      <iframe
        title="GoFundMe fundraiser"
        src={goFundMeWidgetUrl}
        className="h-[680px] w-full border border-[#d8d0be] bg-white"
        loading="lazy"
      />
    </div>
  );
}

function AboutPage() {
  return (
    <div className="min-h-screen bg-[#f8f4ea] text-[#18271e]">
      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto grid max-w-[92rem] gap-10 px-6 py-12 sm:px-10 md:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] md:items-stretch md:py-16 lg:gap-14 lg:px-16">
          <div className="flex max-w-2xl flex-col">
            <div>
              <h1 className="font-serif text-5xl font-normal leading-tight text-[#0c351e] sm:text-6xl md:text-7xl">
                Our story
              </h1>
              <div className="mt-6 h-px w-28 bg-[#304536]" />
              <div className="mt-8 space-y-5 text-lg leading-8 text-[#445044] lg:text-xl lg:leading-9">
                <p>
                  After 23 years in the Marines, Joe Johnson returned to Mount Airy with a simple idea: bring a community garden to the playground where he grew up.
                </p>
                <p>
                  That idea became a place where neighbors grow fresh food, learn organic gardening, and build community together.
                </p>
              </div>
            </div>

            <div className="mt-8 border-y border-[#d8d0be] py-1 md:mt-10 md:py-6">
              <div className="grid divide-y divide-[#d8d0be] text-sm text-[#4f5b4f] md:grid-cols-1 md:gap-5 md:divide-y-0 xl:grid-cols-3">
                <div className="py-4 md:py-0">
                  <p className="font-serif text-2xl leading-tight text-[#10351f] md:text-3xl md:leading-none">54 garden beds</p>
                  <p className="mt-1.5 leading-6 md:mt-2">for community members</p>
                </div>
                <div className="py-4 md:py-0">
                  <p className="font-serif text-2xl leading-tight text-[#10351f] md:text-3xl md:leading-none">Investing in youth</p>
                  <p className="mt-1.5 leading-6 md:mt-2">Summer Garden Assistant Program</p>
                </div>
                <div className="py-4 md:py-0">
                  <p className="font-serif text-2xl leading-tight text-[#10351f] md:text-3xl md:leading-none">50 families served</p>
                  <p className="mt-1.5 leading-6 md:mt-2">through our Free Produce Box Program</p>
                </div>
              </div>
            </div>

            <div className="mt-7 hidden flex-col gap-3 md:flex xl:flex-row">
              <Link
                to="/apply"
                className="inline-flex w-full items-center justify-center gap-2 border border-[#43513f] bg-[#10351f] px-5 py-3 text-sm font-medium text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef] sm:w-auto"
              >
                <span>Join the garden</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
              <Link
                to="/contact"
                className="inline-flex w-full items-center justify-center gap-2 border border-[#c9bfae] px-5 py-3 text-sm font-medium text-[#10351f] transition-colors hover:border-[#8e3a2b] hover:bg-[#f4efe3] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef] sm:w-auto"
              >
                <span>Contact us</span>
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </div>

          <InstagramVideoEmbed />
        </div>
      </section>

      <section className="border-b border-[#d8d0be] bg-[#f8f4ea]">
        <div className="mx-auto grid max-w-[92rem] gap-12 px-6 py-14 sm:px-10 md:grid-cols-[0.9fr_1fr] md:items-center md:py-20 lg:px-16">
          <div className="overflow-hidden border border-[#d3cbbb]">
            <img
              src={kidsWorkingPhoto}
              alt="Young people working in the garden beds at Pleasant Playground Community Garden"
              loading="lazy"
              decoding="async"
              className="aspect-[4/3] w-full object-cover"
            />
          </div>
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
              What we do
            </p>
            <h2 className="mt-3 font-serif text-4xl text-[#10351f] sm:text-5xl">
              Grow food, grow community.
            </h2>
            <div className="mt-6 space-y-5 text-lg leading-8 text-[#445044]">
              <p>
                Limited 4x4 and 8x4 garden plots are available for the 2026 growing season.
              </p>
              <div>
                <p>We welcome individuals and families interested in:</p>
                <ul className="mt-3 list-disc space-y-2 pl-6">
                  <li>Growing fresh, healthy food</li>
                  <li>Connecting with neighbors</li>
                  <li>Learning organic gardening</li>
                </ul>
              </div>
              <p>
                PPCG is rooted in community, culture, and shared knowledge. We are committed
                to ensuring that traditions of growing, especially those rooted in African
                American agricultural practices, continue to thrive.
              </p>
            </div>
            <Link
              to="/apply"
              className="mt-8 inline-flex w-full items-center justify-between border border-[#43513f] bg-[#10351f] px-5 py-4 text-base font-medium capitalize text-[#fbf8ef] transition-colors hover:bg-[#0a2416] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#f8f4ea] sm:w-56"
            >
              <span>Garden Plot Application Form</span>
              <span aria-hidden="true">&rarr;</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-[#d8d0be] bg-[#fbf8ef]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-16 lg:px-16">
          <div className="mb-8 max-w-2xl">
            <h2 className="font-serif text-4xl text-[#10351f] sm:text-5xl">
              In the news
            </h2>
          </div>
          <div className="grid gap-5 md:grid-cols-3">
            {newsArticles.map((article) => (
              <a
                key={article.publication}
                href={article.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Read the Pleasant Playground Community Garden article in ${article.publication}`}
                className={`group flex min-h-40 items-center justify-center border border-[#cfc7b6] ${article.cardClassName || 'bg-white'} p-8 shadow-sm transition-colors duration-200 hover:border-[#8e3a2b] focus:outline-none focus:ring-2 focus:ring-[#8e3a2b] focus:ring-offset-2 focus:ring-offset-[#fbf8ef]`}
              >
                <img
                  src={article.masthead}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className={`${article.mastheadClassName} w-full object-contain transition-transform duration-200 group-hover:scale-[1.02]`}
                />
              </a>
            ))}
          </div>
          <div className="relative mx-auto mt-6 aspect-video max-w-4xl overflow-hidden border border-[#cfc7b6] bg-black shadow-sm">
            <iframe
              title="6abc Action News video about Pleasant Playground Community Garden"
              src={sixAbcVideoEmbedUrl}
              width="640"
              height="360"
              allow="fullscreen; picture-in-picture"
              allowFullScreen
              frameBorder="0"
              loading="lazy"
              className="absolute inset-0 h-full w-full"
            />
          </div>
        </div>
      </section>

      <section className="border-b border-[#d8d0be] bg-[#f4efe3]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-16 lg:px-16">
          <div className="grid gap-10 lg:grid-cols-[minmax(0,0.82fr)_minmax(26rem,1fr)] lg:items-center">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
                Support the garden
              </p>
              <h2 className="mt-3 font-serif text-4xl leading-tight text-[#10351f] sm:text-5xl">
                Help Us Grow Food, Grow Community in 2026
              </h2>
              <div className="mt-6 h-px w-24 bg-[#304536]" />
              <h3 className="mt-8 font-serif text-2xl text-[#10351f]">
                Why We&apos;re Fundraising
              </h3>
              <p className="mt-4 text-lg leading-8 text-[#445044]">
                Your contribution will directly support three critical areas of our work.
              </p>
              <ol className="mt-7 space-y-3 text-base leading-7 text-[#445044]">
                <li className="border-l-4 border-[#8e3a2b] bg-[#fffdf7] px-5 py-4">
                  <span className="block font-semibold text-[#10351f]">1. Sustaining the Garden</span>
                  Daily Operations &amp; Programming
                </li>
                <li className="border-l-4 border-[#8e3a2b] bg-[#fffdf7] px-5 py-4">
                  <span className="block font-semibold text-[#10351f]">2. Expanding Our Free Produce Box Program</span>
                  Serving 50 Families
                </li>
                <li className="border-l-4 border-[#8e3a2b] bg-[#fffdf7] px-5 py-4">
                  <span className="block font-semibold text-[#10351f]">3. Investing in Youth</span>
                  Summer Garden Assistant Program
                </li>
              </ol>
            </div>
            <GoFundMeEmbed />
          </div>
        </div>
      </section>

      <section className="bg-[#f1eadc]">
        <div className="mx-auto max-w-[92rem] px-6 py-14 sm:px-10 md:py-16 lg:px-16">
          <div className="mb-7">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#8e3a2b]">
              In the garden
            </p>
            <h2 className="mt-2 font-serif text-4xl text-[#10351f] sm:text-5xl">
              Gallery
            </h2>
          </div>
          <ImageGallery images={galleryImages} showPagination={true} />
        </div>
      </section>
    </div>
  );
}

export default AboutPage;
